/**
 * Calm Premium — переиспользуемые презентационные примитивы редизайна.
 * Собраны в одном файле т.к. это мелкие визуальные кирпичики, повторяющиеся
 * по многим экранам (icon-chip, моно-лейбл, прогресс-бар, статус-пилюля,
 * выбираемая строка, плитка действия). Логики/данных тут нет — только вид.
 */
import * as React from "react";
import { Check, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/* -------------------------------------------------------------------------- */
/* Tone — тонировка icon-chip / прогресс-баров                                 */
/* -------------------------------------------------------------------------- */
export type Tone = "blue" | "green" | "amber" | "red" | "neutral";

const CHIP_TONE: Record<Tone, { bg: string; fg: string }> = {
  blue: { bg: "rgb(var(--brand-primary-rgb) / 0.12)", fg: "var(--color-accent)" },
  green: { bg: "rgba(56,224,165,0.12)", fg: "#5fe3ad" },
  amber: { bg: "rgba(240,176,78,0.12)", fg: "#f0b04e" },
  red: { bg: "rgba(240,101,106,0.12)", fg: "#f0656a" },
  neutral: { bg: "rgba(255,255,255,0.06)", fg: "#c2c4ca" },
};

/* -------------------------------------------------------------------------- */
/* IconChip — тонированный квадрат-чип с lucide-иконкой                         */
/* -------------------------------------------------------------------------- */
/**
 * Плитка со значком тарифа, который задал партнёр в кабинете.
 *
 * Геометрия та же, что у `IconChip`: значок ЗАМЕНЯЕТ нашу иконку, и при разной
 * высоте сетка карточек перекладывалась бы в зависимости от настроек бота.
 */
export function GlyphChip({
  glyph,
  tone = "blue",
  size = 40,
  radius = 12,
  className,
}: {
  glyph: string;
  tone?: Tone;
  size?: number;
  radius?: number;
  className?: string;
}) {
  const t = CHIP_TONE[tone];
  return (
    <div
      className={cn("flex shrink-0 items-center justify-center", className)}
      style={{ width: size, height: size, borderRadius: radius, background: t.bg }}
    >
      <span style={{ fontSize: Math.round(size * 0.5), lineHeight: 1 }}>{glyph}</span>
    </div>
  );
}

export function IconChip({
  icon: Icon,
  tone = "blue",
  size = 40,
  iconSize,
  radius = 12,
  className,
}: {
  icon: LucideIcon;
  tone?: Tone;
  size?: number;
  iconSize?: number;
  radius?: number;
  className?: string;
}) {
  const t = CHIP_TONE[tone];
  return (
    <div
      className={cn("flex shrink-0 items-center justify-center", className)}
      style={{ width: size, height: size, borderRadius: radius, background: t.bg }}
    >
      <Icon size={iconSize ?? Math.round(size * 0.46)} strokeWidth={1.8} color={t.fg} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* SectionLabel — моно-лейбл секции (JetBrains Mono, uppercase, tracking)       */
/* (трекинг здесь — намеренный дизайн-токен моно-лейбла, не общий КАПС)         */
/* -------------------------------------------------------------------------- */
export function SectionLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("font-mono-label", className)}>{children}</div>;
}

/* -------------------------------------------------------------------------- */
/* ProgressBar — трек + градиент-заливка с glow                                 */
/* -------------------------------------------------------------------------- */
export function ProgressBar({
  value,
  tone = "blue",
  height = 7,
  className,
}: {
  value: number; // 0..1
  tone?: "green" | "blue";
  height?: number;
  className?: string;
}) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  const fill = tone === "green" ? "var(--calm-bar-green)" : "var(--calm-bar-blue)";
  const glow = tone === "green" ? "rgba(56,224,165,0.5)" : "rgba(78,164,240,0.5)";
  return (
    <div
      className={cn("w-full overflow-hidden rounded-full", className)}
      style={{ height, background: "var(--calm-track)" }}
    >
      <div
        className="h-full rounded-full transition-all duration-500 ease-out"
        style={{ width: `${pct}%`, background: fill, boxShadow: `0 0 10px ${glow}` }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* StatusPill — пилюля статуса подписки                                         */
/* -------------------------------------------------------------------------- */
export function StatusPill({
  status,
  label,
  className,
}: {
  status: "active" | "frozen" | "expired";
  label?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  if (status === "active") {
    return (
      <span
        className={cn(
          "inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase",
          className,
        )}
        style={{ background: "rgba(56,224,165,0.12)", color: "#5fe3ad" }}
      >
        <span
          className="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ background: "#38e0a5", boxShadow: "0 0 6px #38e0a5" }}
        />
        <span className="truncate">{label ?? t('Badge active')}</span>
      </span>
    );
  }
  if (status === "frozen") {
    return (
      <span
        className={cn(
          "inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase",
          className,
        )}
        style={{ background: "rgb(var(--brand-primary-rgb) / 0.14)", color: "var(--color-accent)" }}
      >
        <span className="truncate">{label ?? t('Badge frozen')}</span>
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase",
        className,
      )}
      style={{ background: "rgba(255,255,255,0.07)", color: "#8a8c93" }}
    >
      <span className="truncate">{label ?? t('Badge expired')}</span>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* OptionRow — выбираемая строка (провайдер / пакет / тариф)                    */
/* -------------------------------------------------------------------------- */
export function OptionRow({
  icon,
  iconTone = "blue",
  title,
  subtitle,
  trailing,
  selected = false,
  onSelect,
  disabled,
  className,
}: {
  icon?: LucideIcon;
  iconTone?: Tone;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  trailing?: React.ReactNode;
  selected?: boolean;
  onSelect?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        "flex w-full items-center gap-3 rounded-[16px] border p-3 text-left transition-all active:scale-[0.98] disabled:opacity-50",
        selected ? "border-accent bg-primary/8" : "calm-card",
        className,
      )}
    >
      {icon && <IconChip icon={icon} tone={selected ? "blue" : iconTone} size={40} />}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold text-foreground">{title}</div>
        {/* Подпись переносится: «Не хватает 1 491 ₽, пополнить» обрывалась на «по…»,
            и человек не узнавал, сколько не хватает. */}
        {subtitle && <div className="text-[12px] leading-snug text-[#8a8c93]">{subtitle}</div>}
      </div>
      {trailing && <div className="shrink-0 text-right">{trailing}</div>}
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
          selected ? "border-accent bg-accent text-white" : "border-white/15",
        )}
      >
        {selected && <Check size={13} strokeWidth={3} />}
      </span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* ActionTile — плитка сетки действий на главной                               */
/* -------------------------------------------------------------------------- */
export function ActionTile({
  icon,
  label,
  onClick,
  disabled,
  tone = "blue",
  className,
}: {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  tone?: Tone;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex flex-col items-center gap-2 rounded-[16px] p-3 calm-card transition-transform active:scale-[0.96] disabled:opacity-40",
        className,
      )}
    >
      <IconChip icon={icon} tone={tone} size={42} />
      <span className="text-[12px] font-semibold text-[#c2c4ca]">{label}</span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* HeroGlow — размытый цветной круг за hero-блоком                              */
/* -------------------------------------------------------------------------- */
export function HeroGlow({
  color = "var(--color-primary)",
  size = 220,
  blur = 78,
  opacity = 0.18,
  className,
}: {
  color?: string;
  size?: number;
  blur?: number;
  opacity?: number;
  className?: string;
}) {
  return (
    <div
      aria-hidden
      className={cn("pointer-events-none absolute", className)}
      style={{
        width: size,
        height: size,
        borderRadius: "9999px",
        background: color,
        filter: `blur(${blur}px)`,
        opacity,
      }}
    />
  );
}
